import { FormEvent, useRef, useState } from "react";

import { Button, Col, Row, Typography } from "antd";
import { toast } from "utils/toast";

import usePageLoading from "hooks/usePageLoading";

import {
  PageLoadingContainer,
  SkeletonAvatar,
  SkeletonBlock,
  SkeletonButton,
  SkeletonHeading,
  SkeletonInput,
  SkeletonText,
  SlowStateBanner,
} from "js/components/Loading";
import ListMembers from "js/components/Organization/MembersList";
import { Button as RetryButton } from "js/components/ui/button";
import { EmailInput, EmailInputRef } from "js/components/ui/email-input";

import { OrganizationException } from "js/exceptions/OrganizationException";

import {
  useGetMembers,
  useInviteToOrganization,
} from "../../../hooks/useOrganizations";
import { useAppSelector } from "../../../redux/hooks";

const { Title } = Typography;

const Members = () => {
  const [email, setEmail] = useState("");
  const emailInputRef = useRef<EmailInputRef>(null);

  const { data: members, isLoading, refetch: refetchMembers } = useGetMembers();
  const inviteMutation = useInviteToOrganization();

  const { userData } = useAppSelector((state) => state.entities.user);

  const isOwner = userData?.role === "Owner";

  const onDisplayInviteError = (message: string) => {
    toast.error(message);
  };

  const inviteMember = async () => {
    // Validate email using EmailInput ref
    const isValid = await emailInputRef.current?.validate();
    if (!isValid) {
      return;
    }

    const emails: string[] = [email];

    inviteMutation.mutate(emails, {
      onSuccess: () => {
        setEmail(""); // Clear email input after successful invite
      },
      onError: (err: any) => {
        const errorCode =
          err.response.data.code === 4000
            ? "5003"
            : `${err.response.data.code}`;
        const organizationException = new OrganizationException(errorCode);
        onDisplayInviteError(organizationException.message);
      },
    });
  };

  const handleInviteMemberSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void inviteMember();
  };

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading,
  });

  const handleRetryFetch = () => {
    dismissSlowState();
    void refetchMembers();
  };

  const slowStateBanner = showSlowState ? (
    <SlowStateBanner
      actions={
        <RetryButton
          size="sm"
          variant="outline"
          className="border-amber-200 bg-amber-50/70 text-amber-700 hover:bg-amber-50"
          onClick={handleRetryFetch}
        >
          Reintentar
        </RetryButton>
      }
    >
      <p className="font-medium">Esto está tardando más de lo previsto.</p>
      <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
    </SlowStateBanner>
  ) : null;

  const headerSkeleton = (
    <div className="space-y-3">
      <SkeletonHeading className="h-6 w-72" />
    </div>
  );

  const actionsSkeleton = isOwner ? (
    <div className="space-y-4">
      <SkeletonHeading className="h-5 w-96" />
      <div className="flex items-center gap-2">
        <SkeletonInput className="w-80" />
        <SkeletonButton className="w-24" />
      </div>
    </div>
  ) : undefined;

  const contentSkeleton = (
    <div className="space-y-0">
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          key={`member-skeleton-${index}`}
          className="flex items-center gap-6 py-4"
          style={{
            background: index % 2 === 1 ? "#F2F5F9FF" : "#ffffff",
          }}
        >
          <div className="ml-5">
            <SkeletonAvatar className="size-12" />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <SkeletonText className="h-5 w-40" />
            <SkeletonText className="h-4 w-24" />
          </div>
          <div className="flex-1">
            <SkeletonText className="h-5 w-56" />
          </div>
          {isOwner && (
            <div className="mr-4">
              <SkeletonButton className="h-8 w-8" />
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <>
      <PageLoadingContainer
        showSkeleton={showSkeleton}
        slowState={slowStateBanner}
        headerSkeleton={headerSkeleton}
        actionsSkeleton={actionsSkeleton}
        contentSkeleton={contentSkeleton}
        className="space-y-8"
      >
        <div id="members" className="space-y-8">
          <div>
            <Title level={3}>Asesores de la Organización</Title>
          </div>
          {isOwner && (
            <div>
              <Title level={5} style={{ marginBottom: "1rem" }}>
                Invita usuarios a tu organización rápidamente
              </Title>
              <Row justify={"space-between"}>
                <Col>
                  <form
                    className="flex flex-col gap-3 sm:flex-row sm:items-end"
                    onSubmit={handleInviteMemberSubmit}
                  >
                    <div className="w-full sm:w-80">
                      <EmailInput
                        ref={emailInputRef}
                        value={email}
                        onChange={setEmail}
                        label="Correo electrónico"
                        placeholder="correo@ejemplo.com"
                        required
                        showIcon={false}
                        showStatus={false}
                        className="h-10"
                      />
                    </div>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={inviteMutation.isPending}
                    >
                      Invitar
                    </Button>
                  </form>
                </Col>
              </Row>
            </div>
          )}
          <div>
            <ListMembers
              list={members || []}
              initLoading={isLoading}
              isOwner={isOwner}
            />
          </div>
        </div>
      </PageLoadingContainer>
    </>
  );
};

export default Members;
